import { Controller } from "@hotwired/stimulus";
import { inferIdentityQuality } from "../lib/identity_quality";
import { clearFieldErrors } from "../lib/clear_field_errors";

export default class extends Controller {
  static targets = ["quality", "status", "statusLabel"];
  static values = { inputName: String, nameInputs: Array, retainedValue: Boolean, kind: String };

  connect() {
    const form = this.element.closest("form");
    const names = this.hasNameInputsValue ? this.nameInputsValue : [this.inputNameValue];
    this.fieldNames = names;
    this.fields = names.map(name => form?.elements.namedItem(name)).filter(Boolean).map(input => ({
      input,
      initialValue: input.value,
      required: input.required,
      readOnly: input.readOnly,
      placeholder: input.getAttribute("placeholder"),
      savedValue: undefined,
    }));
    this.manualQuality = this.qualityTarget.value !== "";
    this.update();
  }

  disconnect() {
    this.reset();
  }

  reset() {
    // Keep recoverable values only in memory during this editing session.
    this.fields?.forEach(field => {
      field.savedValue = undefined;
      field.initialValue = undefined;
      this.setUnavailable(field, false, false);
    });
  }

  chooseQuality() {
    this.manualQuality = this.qualityTarget.value !== "";
    this.update();
    clearFieldErrors(this.element);
  }

  valueChanged(event) {
    if (this.updating || !this.fieldNames.includes(event.target.name)) return;

    if (!this.manualQuality) {
      const changed = this.fields.some(field => field.input.value !== field.initialValue);
      const values = Object.fromEntries(this.fields.map(({ input }) => [input.name.match(/\[([^\]]+)\]$/)[1], input.value]));
      this.qualityTarget.value = changed ? inferIdentityQuality(this.kindValue, values) : "";
    }
    this.updateStatus();
    clearFieldErrors(this.element);
  }

  update() {
    this.updating = true;
    this.updateStatus();
    const missing = ["8", "9", "99"].includes(this.qualityTarget.value);
    // Unknown/refused names may retain a nickname; not-collected clears the group.
    const unavailable = this.hasNameInputsValue ? this.qualityTarget.value === "99" : missing;
    this.fields?.forEach(field => {
      this.updateField(field, unavailable);
      this.setUnavailable(field, unavailable, missing);
    });
    this.updating = false;
  }

  updateField(field, unavailable) {
    const previousValue = field.input.value;
    if (unavailable) {
      field.savedValue ??= previousValue;
      field.input.value = "";
    } else if (field.savedValue !== undefined) {
      field.input.value = field.savedValue;
      field.savedValue = undefined;
    }
    if (field.input.value !== previousValue) {
      field.input.dispatchEvent(new Event("input", { bubbles: true }));
      field.input.dispatchEvent(new Event("change", { bubbles: true }));
    }
  }

  updateStatus() {
    this.statusTarget.dataset.quality = this.qualityTarget.value;
    const answered = this.retainedValueValue || this.fields.some(field => field.input.value.trim() !== "");
    const blankStatus = answered ? this.qualityTarget.dataset.qualityMissing : this.qualityTarget.dataset.unanswered;
    this.statusLabelTarget.textContent = this.qualityTarget.selectedOptions[0].dataset.status || blankStatus;
  }

  setUnavailable(field, unavailable, missing) {
    // Readonly fields still submit the blank value that clears the saved answer.
    field.input.readOnly = unavailable || field.readOnly;
    field.input.required = !missing && field.required;
    field.input.classList.toggle("bg-base-200", unavailable);
    field.input.classList.toggle("text-base-content/60", unavailable);
    field.input.classList.toggle("border-dashed", unavailable);
    if (unavailable) {
      field.input.setAttribute("placeholder", this.qualityTarget.selectedOptions[0].textContent);
    } else if (field.placeholder === null) {
      field.input.removeAttribute("placeholder");
    } else {
      field.input.setAttribute("placeholder", field.placeholder);
    }
  }
}
